import * as preact from "preact";
import { h } from "preact";

import * as modalController from "modalController";
import * as bookUtils from "shared/bookUtils";
import * as languageMap from "shared/languageMap";
import type { BookData } from "stores/BookStore";
import BookStore from "stores/BookStore";
import stores from "stores/stores";
import { AIPolishDialog } from "./AIPolishDialog";

interface NotificationBar {
  showMessage(options: { message: string; element: JQuery }): void;
}

interface EditDocumentProps {
  model: BookStore;
  notificationBar?: NotificationBar;
  /** Called after a successful save, e.g. to close a containing dialog */
  onSaved?: () => void;
  refreshPage: (options?: { reInitialize?: boolean }) => Promise<void> | void;
}

interface EditDocumentForm {
  title: string;
  author: string;
  plainText: string;
  ignoreSingleNewLines: boolean;
  category: string;
  language: string;
  aiGenerated: boolean;
}

interface EditDocumentState {
  form: EditDocumentForm;
  initialForm: EditDocumentForm;
  loading: boolean;
  saving: boolean;
}

const useEveryNewLineToIgnoreSingleNewLines = (
  useEveryNewLine: BookData["useEveryNewLine"]
) => {
  if (typeof useEveryNewLine === "boolean") {
    return !useEveryNewLine;
  }
  if (typeof useEveryNewLine === "string") {
    return !JSON.parse(useEveryNewLine);
  }
  return false;
};

const formFromBook = (book: BookData): EditDocumentForm => ({
  title: book.title ?? "",
  author: book.author ?? "",
  plainText: book.plainText ?? "",
  ignoreSingleNewLines: useEveryNewLineToIgnoreSingleNewLines(
    book.useEveryNewLine
  ),
  category: book.category ?? "",
  language: book.language ?? "",
  aiGenerated: Boolean(book.aiGenerated),
});

const formsEqual = (left: EditDocumentForm, right: EditDocumentForm) =>
  left.title === right.title &&
  left.author === right.author &&
  left.plainText === right.plainText &&
  left.ignoreSingleNewLines === right.ignoreSingleNewLines &&
  left.category === right.category &&
  left.language === right.language &&
  left.aiGenerated === right.aiGenerated;

export class EditDocument extends preact.Component<
  EditDocumentProps,
  EditDocumentState
> {
  state: EditDocumentState = (() => {
    const form = formFromBook(this.props.model.book.value);
    return {
      form,
      initialForm: form,
      loading: !("plainText" in this.props.model.book.value),
      saving: false,
    };
  })();

  async componentDidMount() {
    if (!("plainText" in this.props.model.book.value)) {
      await this.props.model.fetchField("plainText");
      const form = formFromBook(this.props.model.book.value);
      this.setState({ form, initialForm: form, loading: false });
    }
  }

  isOwner() {
    const ownerID = this.props.model.book.value.ownerID;
    return (
      (ownerID != null &&
        stores.user.state.value._id?.toString() === ownerID.toString()) ||
      stores.user.isGod.value
    );
  }

  hasChanges(form = this.state.form) {
    return !formsEqual(form, this.state.initialForm);
  }

  updateField = (field: keyof EditDocumentForm) => (event: Event) => {
    const target = event.currentTarget as HTMLInputElement;
    const value = target.type === "checkbox" ? target.checked : target.value;
    this.setState({
      form: {
        ...this.state.form,
        [field]: value,
      },
    } as Pick<EditDocumentState, "form">);
  };

  preventCtrlSpaceDefault = (event: KeyboardEvent) => {
    if (event.ctrlKey && event.which === " ".charCodeAt(0)) {
      event.preventDefault();
    }
  };

  undoChanges = () => {
    this.setState({ form: this.state.initialForm });
  };

  openAIPolishDialog = () => {
    const currentText = this.state.form.plainText;

    if (!currentText || currentText.trim() === "") {
      alert("Please enter some text to polish");
      return;
    }

    const bookLanguage = this.state.form.language;
    const isRTL = languageMap.map[bookLanguage]?.direction === "RTL";
    const book = this.props.model.book.value;
    const isTranscript = Boolean(book.youTubeID || book.audioFileID);

    modalController.show(
      h(AIPolishDialog, {
        originalText: currentText,
        isRTL,
        isTranscript,
        onSave: (polishedText) => {
          const form = { ...this.state.form, plainText: polishedText };
          this.setState({ form });
          void this.saveDocument(form);
          modalController.cancel();
        },
        onSaveAsNew: () => {},
        onCancel: () => {
          modalController.cancel();
        },
      }),
      {
        clickToCancel: false,
      }
    );
  };

  saveDocument = async (form = this.state.form) => {
    const book = this.props.model.book.value;
    const newValues: Partial<BookData> = {};

    if (form.title !== book.title) {
      newValues.title = form.title;
    }
    if (form.author !== book.author) {
      newValues.author = form.author;
    }
    if (form.plainText !== book.plainText) {
      newValues.plainText = form.plainText;
      newValues.generatedVersion = 0;
    }

    const useEveryNewLine = !form.ignoreSingleNewLines;
    if (book.useEveryNewLine !== useEveryNewLine) {
      newValues.useEveryNewLine = useEveryNewLine;
      newValues.generatedVersion = 0;
    }

    if (form.category && book.category !== form.category) {
      newValues.category = form.category;
    }

    if (form.language && book.language !== form.language) {
      newValues.language = form.language;
    }

    if (Boolean(book.aiGenerated) !== form.aiGenerated) {
      newValues.aiGenerated = form.aiGenerated;
    }

    this.setState({ saving: true });

    try {
      await this.props.model.patchBook(newValues);

      if (
        this.props.notificationBar &&
        (newValues.plainText || newValues.useEveryNewLine !== undefined)
      ) {
        let message = "Document Saved.";
        if (book.youTubeID) {
          message =
            "Document Saved. You may need to re-sync the text with the audio.";
        }
        this.props.notificationBar.showMessage({
          message,
          element: $(".container"),
        });
      }

      await this.props.model.fetch();
      if (!("plainText" in this.props.model.book.value)) {
        this.props.model.book.value = {
          ...this.props.model.book.value,
          plainText: form.plainText,
        };
      }
      const savedForm = formFromBook(this.props.model.book.value);
      // The callback form guarantees hasChanges() is false before onSaved
      // closes a containing dialog, so its unsaved-changes prompt can't fire
      this.setState(
        {
          form: savedForm,
          initialForm: savedForm,
          saving: false,
        },
        () => {
          if (newValues.language) {
            window.location.reload();
          } else {
            this.props.refreshPage();
            this.props.onSaved?.();
          }
        }
      );
    } catch (error: any) {
      this.setState({ saving: false });
      console.log("error: ", error);
      if (!error?.jqXHR?.alreadyNotified) {
        alert(error?.jqXHR?.data?.error ?? "Failed to save document");
      }
    }
  };

  renderOwnerForm() {
    const bookLanguage = this.state.form.language;
    const isRTL = languageMap.map[bookLanguage]?.direction === "RTL";
    const hasChanges = this.hasChanges();
    const showAIPolishButton = stores.user.hasPremiumAccess.value;

    return (
      <>
        <div class="editDocumentHeader">
          <table>
            <tbody>
              <tr>
                <td>
                  <h4>Title</h4>
                </td>
                <td>
                  <input
                    id="titleInput"
                    onInput={this.updateField("title")}
                    type="text"
                    value={this.state.form.title}
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <h4>Author</h4>
                </td>
                <td class="authorRow">
                  <input
                    id="authorInput"
                    maxLength={bookUtils.maxAuthorLength}
                    onInput={this.updateField("author")}
                    type="text"
                    value={this.state.form.author}
                  />
                  <label
                    class="aiCheckboxLabel"
                    title="Was this text generated by AI?"
                  >
                    AI{" "}
                    <input
                      checked={this.state.form.aiGenerated}
                      id="aiGeneratedCheckbox"
                      onChange={this.updateField("aiGenerated")}
                      type="checkbox"
                    />
                  </label>
                </td>
              </tr>
              <tr>
                <td>
                  <h4>Category</h4>
                </td>
                <td>
                  <select
                    id="categorySelect"
                    onChange={this.updateField("category")}
                    value={this.state.form.category}
                  >
                    <option class="dummy" value="">
                      Choose a category...
                    </option>
                    {bookUtils.categories.map((name) => (
                      <option key={name} value={name}>
                        {bookUtils.categoryLabel(name)}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
              <tr>
                <td>
                  <h4>Language</h4>
                </td>
                <td>
                  <select
                    id="bookLanguageSelect"
                    onChange={this.updateField("language")}
                    value={this.state.form.language}
                  >
                    <option class="dummy" value="">
                      Choose a language...
                    </option>
                    {[...languageMap.learningList]
                      .sort((a, b) => a.english.localeCompare(b.english))
                      .map((language) => (
                        <option key={language.code} value={language.code}>
                          {language.english} ({language.name})
                        </option>
                      ))}
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="editDocumentContent">
          <div class="documentTextHeader">
            <h4>Document text</h4>
            {showAIPolishButton ? (
              <button
                class="aiPolishButton"
                id="aiPolishButton"
                onClick={this.openAIPolishDialog}
              >
                <i class="icon-magic"></i> AI Refine
              </button>
            ) : null}
          </div>
          <textarea
            dir={isRTL ? "rtl" : undefined}
            id="documentText"
            onInput={this.updateField("plainText")}
            onKeyDown={this.preventCtrlSpaceDefault}
            value={this.state.form.plainText}
          />
          <label>
            Ignore single new lines:{" "}
            <input
              checked={this.state.form.ignoreSingleNewLines}
              id="ignoreSingleNewLines"
              onChange={this.updateField("ignoreSingleNewLines")}
              type="checkbox"
            />
          </label>
        </div>
        <div class="editDocumentFooter">
          <button
            class="prominent"
            disabled={!hasChanges || this.state.saving}
            id="saveDocumentButton"
            onClick={() => void this.saveDocument()}
          >
            {this.state.saving ? "Saving..." : "Save"}
          </button>
          <button
            disabled={!hasChanges || this.state.saving}
            id="undoDocumentChangesButton"
            onClick={this.undoChanges}
          >
            Undo
          </button>
        </div>
      </>
    );
  }

  render() {
    if (this.state.loading) {
      return null;
    }

    if (!this.isOwner()) {
      return <>You can only edit documents that you have created.</>;
    }

    return this.renderOwnerForm();
  }
}
